import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const SendInvoiceModal = ({ isOpen, onClose, invoice, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    subject: '',
    body: '',
    attachPdf: true,
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && invoice) {
      fetchTemplates();
    }
  }, [isOpen, invoice]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data: orgData } = await supabase?.from('organizations')?.select('id, name')?.limit(1)?.single();
      const { data, error } = await supabase
        ?.from('email_templates')
        ?.select('*')
        ?.eq('organization_id', orgData?.id)
        ?.in('template_type', ['invoice_sent', 'payment_reminder', 'overdue_notice'])
        ?.eq('is_active', true);

      if (error) throw error;
      setTemplates(data || []);

      // Auto-select invoice_sent template
      const defaultTmpl = data?.find(t => t?.template_type === 'invoice_sent') || data?.[0];
      if (defaultTmpl) {
        applyTemplate(defaultTmpl, orgData?.name);
        setSelectedTemplate(defaultTmpl?.id);
      } else {
        // Fallback defaults
        setEmailData(prev => ({
          ...prev,
          to: invoice?.customer?.email || '',
          subject: `Invoice ${invoice?.invoice_number} - Amount Due: $${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}`,
          body: `Dear ${invoice?.customer?.name},\n\nPlease find your invoice ${invoice?.invoice_number} attached.\n\nTotal Amount: $${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}\nDue Date: ${invoice?.due_date ? new Date(invoice?.due_date)?.toLocaleDateString() : 'N/A'}\n\nThank you for your business.`,
        }));
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template, companyName) => {
    const daysOverdue = invoice?.due_date
      ? Math.max(0, Math.floor((new Date() - new Date(invoice?.due_date)) / (1000 * 60 * 60 * 24)))
      : 0;

    const vars = {
      customer_name: invoice?.customer?.name || '',
      invoice_number: invoice?.invoice_number || '',
      invoice_date: invoice?.created_at ? new Date(invoice?.created_at)?.toLocaleDateString() : '',
      due_date: invoice?.due_date ? new Date(invoice?.due_date)?.toLocaleDateString() : 'N/A',
      total_amount: parseFloat(invoice?.total_amount || 0)?.toFixed(2),
      amount_due: parseFloat(invoice?.amount_due || 0)?.toFixed(2),
      days_overdue: daysOverdue?.toString(),
      company_name: companyName || 'Our Company',
    };

    let subject = template?.subject || '';
    let body = template?.body_html || template?.body_text || '';

    Object.entries(vars)?.forEach(([key, val]) => {
      subject = subject?.replace(new RegExp(`{{${key}}}`, 'g'), val);
      body = body?.replace(new RegExp(`{{${key}}}`, 'g'), val);
    });

    setEmailData(prev => ({
      ...prev,
      to: invoice?.customer?.email || '',
      subject,
      body,
    }));
  };

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplate(templateId);
    const tmpl = templates?.find(t => t?.id === templateId);
    if (tmpl) {
      const { data: orgData } = await supabase?.from('organizations')?.select('name')?.limit(1)?.single();
      applyTemplate(tmpl, orgData?.name);
    }
  };

  const handleSend = async () => {
    if (!emailData?.to) { setError('Recipient email is required'); return; }
    if (!emailData?.subject) { setError('Subject is required'); return; }
    setError('');
    setSending(true);
    try {
      // Update invoice sent_at and status
      await supabase?.from('invoices')?.update({
        sent_at: new Date()?.toISOString(),
        sent_to_email: emailData?.to,
        invoice_status: 'sent',
        updated_at: new Date()?.toISOString(),
      })?.eq('id', invoice?.id);

      // Attempt to send via edge function
      try {
        await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-quote-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            type: 'invoice_sent',
            recipientEmail: emailData?.to,
            recipientName: invoice?.customer?.name,
            subject: emailData?.subject,
            body: emailData?.body,
            invoiceNumber: invoice?.invoice_number,
            totalAmount: invoice?.total_amount,
            dueDate: invoice?.due_date,
          }),
        });
      } catch (emailErr) {
        console.warn('Email send failed (non-critical):', emailErr);
      }

      onSent?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const templateTypeLabels = {
    invoice_sent: 'Invoice Notification',
    payment_reminder: 'Payment Reminder',
    overdue_notice: 'Overdue Notice',
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Send" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Send Invoice</h2>
              <p className="text-sm text-muted-foreground">{invoice?.invoice_number} · {invoice?.customer?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading templates...</span>
            </div>
          ) : (
            <>
              {/* Template Selector */}
              {templates?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Email Template</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {templates?.map(tmpl => (
                      <button
                        key={tmpl?.id}
                        onClick={() => handleTemplateChange(tmpl?.id)}
                        className={`p-3 rounded-lg border text-left transition-smooth ${
                          selectedTemplate === tmpl?.id
                            ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon
                            name={tmpl?.template_type === 'invoice_sent' ? 'FileText' : tmpl?.template_type === 'payment_reminder' ? 'Clock' : 'AlertTriangle'}
                            size={14}
                            className={selectedTemplate === tmpl?.id ? 'text-primary' : 'text-muted-foreground'}
                          />
                          <span className="text-xs font-medium">{templateTypeLabels?.[tmpl?.template_type] || tmpl?.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">To <span className="text-error">*</span></label>
                    <input
                      type="email"
                      value={emailData?.to}
                      onChange={e => setEmailData(prev => ({ ...prev, to: e?.target?.value }))}
                      placeholder="customer@example.com"
                      className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">CC (Optional)</label>
                    <input
                      type="email"
                      value={emailData?.cc}
                      onChange={e => setEmailData(prev => ({ ...prev, cc: e?.target?.value }))}
                      placeholder="cc@example.com"
                      className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Subject <span className="text-error">*</span></label>
                  <input
                    type="text"
                    value={emailData?.subject}
                    onChange={e => setEmailData(prev => ({ ...prev, subject: e?.target?.value }))}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-foreground">Email Body</label>
                    <button
                      onClick={() => setPreviewMode(p => !p)}
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <Icon name={previewMode ? 'Edit' : 'Eye'} size={12} />
                      {previewMode ? 'Edit' : 'Preview'}
                    </button>
                  </div>
                  {previewMode ? (
                    <div
                      className="min-h-[200px] p-4 bg-background border border-border rounded-lg prose prose-sm max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: emailData?.body }}
                    />
                  ) : (
                    <textarea
                      value={emailData?.body}
                      onChange={e => setEmailData(prev => ({ ...prev, body: e?.target?.value }))}
                      rows={8}
                      className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                    />
                  )}
                </div>

                {/* PDF Attachment Option */}
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                  <div
                    onClick={() => setEmailData(prev => ({ ...prev, attachPdf: !prev?.attachPdf }))}
                    className={`w-10 h-5 rounded-full transition-smooth cursor-pointer flex-shrink-0 ${
                      emailData?.attachPdf ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <div
                      className="w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-smooth"
                      style={{ marginLeft: emailData?.attachPdf ? '22px' : '2px' }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Attach PDF Invoice</p>
                    <p className="text-xs text-muted-foreground">Include a PDF copy of the invoice as an attachment</p>
                  </div>
                  <Icon name="Paperclip" size={16} className="ml-auto text-muted-foreground" />
                </div>

                {/* Invoice Summary */}
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="text-sm font-medium text-foreground mb-3">Invoice Summary</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Invoice #</p>
                      <p className="text-sm font-medium text-foreground">{invoice?.invoice_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="text-sm font-medium text-primary">${parseFloat(invoice?.total_amount || 0)?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Amount Due</p>
                      <p className="text-sm font-medium text-error">${parseFloat(invoice?.amount_due || 0)?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Due Date</p>
                      <p className="text-sm font-medium text-foreground">
                        {invoice?.due_date ? new Date(invoice?.due_date)?.toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-between">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            iconName="Send"
            iconPosition="left"
            loading={sending}
            disabled={sending || loading}
          >
            Send Invoice
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceModal;
